import { AxiosResponse } from 'axios'
import { actionTree, getterTree, mutationTree } from 'typed-vuex'
import Vue from 'vue'

import { SCHOLARSHIP_STATUS } from '~/lib/utils/filters'
import { setState, toFormData } from '~/lib/utils/store'
import { SCHOLARSHIP_LIST } from '~/store/cardList'
import { typesName } from '~/store/details'

const requirementsTitleMap = {
  essay: 'Essay',
  resume: 'Document',
  'recommendation letter': 'Document',
  cv: 'Document',
  'cover letter': 'Document',
  bio: 'Document',
  video: 'Video',
  'class schedule': 'Document',
  'proof of acceptance': 'Document',
  'proof of enrollment': 'Document',
  profilepic: 'Photo',
  'generic picture': 'Photo',
  'video link': 'Link',
  link: 'Link',
  input: 'Question',
  transcript: 'Document',
  'special eligibility': 'Special eligibility',
  survey: 'Survey',
  goal: 'Goal'
}

const idNames = {
  text: 'requirementTextId',
  file: 'requirementFileId',
  image: 'requirementImageId',
  input: 'requirementInputId',
  survey: 'requirementId',
  'special-eligibility': 'requirementId',
  goal: 'requirementId'
}

export const REQ_TYPES = {
  FILE: 'file',
  TEXT: 'text',
  IMAGE: 'image',
  INPUT: 'input',
  SURVEY: 'survey',
  SPECIAL_ELIGIBILITY: 'special-eligibility',
  GOAL: 'goal'
}

export const state = () => ({
  scholarshipViewedOutOfDetailsPageId: null,
  loading: false,
  requirements: {},
  applications: {}
})

export const getRequirementMappedTitle = (title: string) => {
  return requirementsTitleMap[title.toLowerCase()]
    ? requirementsTitleMap[title.toLowerCase()]
    : title
}

export const getters = getterTree(state, {
  scholarshipId({ scholarshipViewedOutOfDetailsPageId }, _getters, { route }) {
    return scholarshipViewedOutOfDetailsPageId || route.params.cardId
  },
  applicationStatus({ requirements, applications }, { scholarshipId }) {
    if (!scholarshipId) { return SCHOLARSHIP_STATUS.INCOMPLETE }

    requirements = requirements[scholarshipId]
    applications = applications[scholarshipId]

    if (!requirements || !applications) { return SCHOLARSHIP_STATUS.INCOMPLETE }

    if (!Object.keys(requirements).length) { return SCHOLARSHIP_STATUS.READY_TO_SUBMIT }
    if (!Object.keys(applications).length) { return SCHOLARSHIP_STATUS.INCOMPLETE }

    const isReady = Object.keys(requirements).every((name) => {
      if (requirements[name].isOptional) { return true }

      if (requirements[name].type === 'survey' && !!applications[name]) {
        return requirements[name].survey.length === applications[name].answers.length
      }

      return !!applications[name]
    })

    return isReady ? SCHOLARSHIP_STATUS.READY_TO_SUBMIT : SCHOLARSHIP_STATUS.IN_PROGRESS
  },
  isApplicationReady(_state, { applicationStatus }) {
    return applicationStatus === SCHOLARSHIP_STATUS.READY_TO_SUBMIT
  },
  currentRequirements({ requirements }, { scholarshipId }) {
    return scholarshipId ? requirements[scholarshipId] : null
  },
  currentApplications({ applications }, { scholarshipId }) {
    return scholarshipId ? applications[scholarshipId] : null
  },
  getRequirementMappedTitle: () => getRequirementMappedTitle,
  getRequirement({ requirements }) {
    return ({ scholarshipId, requirementType, requirementId }) => {
      if (!requirements[scholarshipId]) { return }
      return requirements[scholarshipId][requirementType + '' + requirementId]
    }
  },
  getApplication({ applications }) {
    return ({ scholarshipId, requirementType, requirementId }) => {
      if (!applications[scholarshipId]) { return }
      return applications[scholarshipId][requirementType + '' + requirementId]
    }
  }
})

export const mutations = mutationTree(state, {
  setState,
  setAttachment(state, { stateName, scholarshipId, key, value }) {
    if (!state[stateName][scholarshipId]) {
      Vue.set(state[stateName], scholarshipId, {})
    }

    if (key) {
      Vue.set(state[stateName][scholarshipId], key, value)
    }
  },
  removeAttachment(state, { stateName, scholarshipId }) {
    delete state[stateName][scholarshipId]
  },
  setScholarshipPreviousStatus(state, { status, scholarshipId }) {
    state.applications[scholarshipId].scholarshipPreviousStatus = status
  }
})

export const actions = actionTree(
  { state, getters, mutations },
  {
    setRequirement({ commit }, { scholarshipId, requirements, applications }): void {
      if (!Object.keys(requirements).length) {
        commit('setAttachment', { stateName: 'requirements', scholarshipId })
        commit('setAttachment', { stateName: 'applications', scholarshipId })
      } else {
        for (const type in requirements) {
          requirements[type].forEach((item) => {
            const key = item.type + item.id
            commit('setAttachment', { stateName: 'requirements', scholarshipId, key, value: item })

            const application = applications[type] && applications[type].find(app => item.id === app[idNames[item.type]])
            commit('setAttachment', { stateName: 'applications', scholarshipId, key, value: application })
          })
        }
      }
    },
    removeRequirement({ commit }, { scholarshipId }): void {
      commit('removeAttachment', { stateName: 'requirements', scholarshipId })
      commit('removeAttachment', { stateName: 'applications', scholarshipId })
    },
    setApplication({ commit }, { scholarshipId, requirementType, requirementId, application }): void {
      const key = requirementType + requirementId
      commit('setAttachment', { stateName: 'applications', scholarshipId, key, value: application })
    },
    removeApplication({ commit }, { scholarshipId, key }): void {
      commit('setAttachment', { stateName: 'applications', scholarshipId, key, value: null })
    },
    applyRequirement(
      { commit, state, rootState },
      { ctrlMethodName, data, req }
    ): Promise<AxiosResponse<any, any>> {
      const formData = toFormData(data)
      commit('setState', { stateName: 'loading', data: true })
      return this.$api.scholarship[ctrlMethodName](formData)
        .then((response) => {
          if (response.data?.data?.scholarship) {
            const applications = response.data.data.scholarship.application
            const application = applications[typesName[req.type]].find(app => app[idNames[req.type]] === req.id)
            const scholarshipId = response.data.data.scholarship.scholarshipId
            const requirement = req
            const currentStatus = applications.status
            const prevStatus = state.applications[scholarshipId]?.scholarshipPreviousStatus ??
             rootState.details.scholarships[scholarshipId]?.scholarship.applicationStatus

            const externalScholarshipId = rootState.details.scholarships[scholarshipId]?.scholarship.externalScholarshipId

            if (prevStatus === SCHOLARSHIP_STATUS.INCOMPLETE && prevStatus !== currentStatus) {
              if (externalScholarshipId) {
                this.app.$accessor.statistic.statStartedScholarship({
                  sid: externalScholarshipId
                })
              }
            }

            commit('setScholarshipPreviousStatus', { status: currentStatus, scholarshipId })

            const isApplicationExist = !!this.app.$accessor.requirements.getApplication({
              scholarshipId: req.scholarshipId,
              requirementType: req.type,
              requirementId: req.id
            })
            if (!isApplicationExist) {
              const scholarship = this.app.$accessor.cardList.getListItem({ storeName: SCHOLARSHIP_LIST, id: req.scholarshipId })
              if (scholarship) {
                this.app.$accessor.cardList.changeItemProperty({
                  storeName: SCHOLARSHIP_LIST,
                  id: scholarship.scholarshipId,
                  propName: 'requirementApplicationCount',
                  propValue: scholarship.requirementApplicationCount + 1
                })
              }
            }

            if (requirement.type === 'survey') {
              if (application !== null && application?.answers.length === requirement.survey.length) {
                this.app.$accessor.requirements.setApplication({
                  scholarshipId,
                  requirementType: requirement.type,
                  requirementId: requirement.id,
                  application
                })
              }
            } else {
              this.app.$accessor.requirements.setApplication({
                scholarshipId,
                requirementType: requirement.type,
                requirementId: requirement.id,
                application
              })
            }

            setTimeout(() => {
              let eventName = this.$mixpanel.events.REQUIREMENT_COMPLETED
              const reqType = requirement.type

              const app = this.app.$accessor.requirements.getApplication({
                scholarshipId,
                requirementType: requirement.type,
                requirementId: requirement.id
              })

              if (['special-eligibility', 'survey', 'input'].includes(reqType)) {
                eventName = this.$mixpanel.events.REQUIREMENT_COMPLETED
              } else if (['file', 'image'].includes(reqType) || (reqType === 'text' && req.allowFile && app.accountFile)) {
                eventName = this.$mixpanel.events.REQUIREMENT_UPLOADED
              } else {
                eventName = ''
              }

              if (eventName) {
                this.$mixpanel.track(
                  eventName,
                  this.$mixpanel.fillProps(eventName, {
                    scholarshipId,
                    requirementId: req.id,
                    requirementType: req.type
                  })
                )
              }
            }, 0)
          }

          return response
        })
        .finally(() => {
          commit('setState', { stateName: 'loading', data: false })
        })
    },
    deleteRequirement({ commit, getters, dispatch }, { ctrlMethodName, applicationId, req }): Promise<AxiosResponse<any, any>> {
      return this.$api.scholarship[ctrlMethodName]({ id: applicationId })
        .then((response) => {
          this.app.$accessor.requirements.removeApplication({
            scholarshipId: req.scholarshipId,
            key: req.type + req.id
          })

          const scholarship = this.app.$accessor.cardList.getListItem({ storeName: SCHOLARSHIP_LIST, id: req.scholarshipId })
          if (scholarship) {
            this.app.$accessor.cardList.changeItemProperty({
              storeName: SCHOLARSHIP_LIST,
              id: scholarship.scholarshipId,
              propName: 'requirementApplicationCount',
              propValue: scholarship.requirementApplicationCount - 1
            })
          }

          return response
        })
    }
  }
)
